import type { AppState } from '@/lib/store/types';

type DiscoveryState = AppState['discovery'];

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

function sanitizeDiscoveredSteps(value: unknown): string[] | undefined {
    if (!Array.isArray(value)) return undefined;
    const steps = value.filter((step): step is string => typeof step === 'string' && step.length > 0);
    return steps.length > 0 ? steps : [];
}

export function buildProfileDiscoveryState(
    existing: unknown,
    discovery: DiscoveryState,
    hasSeenWalkthrough: boolean | undefined
): Record<string, unknown> {
    const base = isRecord(existing) ? existing : {};

    return {
        ...base,
        persona: discovery.persona,
        discoveredSteps: [...discovery.discoveredSteps],
        rewardType: discovery.rewardType,
        currentStepIdx: discovery.currentStepIdx,
        walkthroughCompleted: Boolean(hasSeenWalkthrough),
    };
}

export function parseProfileDiscoveryState(value: unknown): {
    walkthroughCompleted?: boolean;
    discovery?: Partial<DiscoveryState>;
} {
    if (!isRecord(value)) return {};

    const walkthroughCompleted =
        typeof value.walkthroughCompleted === 'boolean' ? value.walkthroughCompleted : undefined;

    const discovery: Partial<DiscoveryState> = {};
    let hasDiscoveryPatch = false;

    if (value.persona === 'normal' || value.persona === 'tech') {
        discovery.persona = value.persona;
        hasDiscoveryPatch = true;
    }

    const discoveredSteps = sanitizeDiscoveredSteps(value.discoveredSteps);
    if (discoveredSteps !== undefined) {
        discovery.discoveredSteps = discoveredSteps;
        hasDiscoveryPatch = true;
    }

    if (value.rewardType === 'sync' || value.rewardType === 'print' || value.rewardType === null) {
        discovery.rewardType = value.rewardType;
        hasDiscoveryPatch = true;
    }

    if (typeof value.currentStepIdx === 'number' && Number.isInteger(value.currentStepIdx) && value.currentStepIdx >= 0) {
        discovery.currentStepIdx = value.currentStepIdx;
        hasDiscoveryPatch = true;
    }

    return {
        walkthroughCompleted,
        discovery: hasDiscoveryPatch ? discovery : undefined,
    };
}
